/**
 * Teammate Handlers (P5.4 Agent Teams) — SUBAGENT_SPAWNED / SUBAGENT_MESSAGE.
 *
 * A teammate is a task-scoped actor with its OWN taskId. Its thread is a REAL
 * (hidden) TaskState in the tasks Map, keyed by teammateTaskId and rendered
 * embedded under the parent's TeammateCard — so the teammate's standard
 * streaming events (AGENT_START/END, TEXT_DELTA, SESSION_STATE_CHANGED,
 * INTERACTION_REQUIRED, MESSAGE_SYNC) land through the EXISTING per-task
 * handlers with zero new plumbing. This module only maintains the registry
 * (hidden lane mint + parent teammates map + reverse index) and appends the
 * relayed lead↔teammate messages.
 */

import type { Socket } from "socket.io-client";
import { EVENTS } from "@/constants/events";
import type {
  SubagentSpawnedPayload,
  SubagentMessagePayload,
} from "@bash/shared";
import { senderFromPeer } from "@bash/shared";
import { useTaskStore, buildNewTask } from "../taskStore";
import type { Message, ContentBlock } from "../types";
import { scanDelegatedSpawns } from "@/lib/teammates";
import { findMessageByAnyId } from "../helpers/findMessage";
import { getSocket } from "../socketService";

export interface EnsureTeammateLaneArgs {
  teammateTaskId: string;
  name: string;
  parentTaskId: string;
  /** Parent Agent tool_use id — '' when unknown (message raced the spawn). */
  toolUseId: string;
  agentSlug?: string;
  agentIcon?: string;
  agentColor?: string;
  runtime?: string;
  /** Phase 2 — 'helper' opens a BACKGROUND HELPER lane (counted in the composer's
   *  "N helpers working") instead of a teammate lane. */
  kind?: "teammate" | "helper";
  /** Phase 2 — the helper's brief; its lane's title. */
  description?: string;
  /** Phase 3 (§4.6) — the person can message this helper, so the composer's @ list
   *  offers it. Carried by both sources (the live spawn and the reload marker). */
  addressable?: boolean;
}

/**
 * Idempotently register one teammate lane:
 *  (a) mint the hidden TaskState (teammateMeta marks it as a lane),
 *  (b) stamp the parent's `teammates` registry,
 *  (c) maintain the store-level reverse index for O(1) landing.
 * Safe to call from the live spawn event (re-emits on reconnect are expected —
 * the sandbox resyncs spawns), the reload scan, and the TeammateCard mount.
 */
export function ensureTeammateLane(args: EnsureTeammateLaneArgs): void {
  const { teammateTaskId, name, parentTaskId, toolUseId } = args;
  if (!teammateTaskId || !parentTaskId || teammateTaskId === parentTaskId)
    return;

  const store = useTaskStore.getState();
  const existing = store.tasks.get(teammateTaskId);
  const parent = store.tasks.get(parentTaskId);
  const parentEntry = parent?.teammates?.[teammateTaskId];
  const indexed = store.teammateParentByTaskId[teammateTaskId];

  const laneUpToDate =
    existing?.teammateMeta &&
    (existing.teammateMeta.parentToolUseId === toolUseId || !toolUseId);
  if (
    laneUpToDate &&
    parentEntry &&
    (parentEntry.toolUseId === toolUseId || !toolUseId) &&
    indexed === parentTaskId
  ) {
    return;
  }

  useTaskStore.setState((state) => {
    const task = state.tasks.get(teammateTaskId);
    // Phase 2 — the helper marks. A lane minted by a racing message knows
    // neither, so the spawn's values win and an already-known value survives a
    // re-mint. Conditional spreads, never `kind: undefined`: a teammate lane
    // keeps the exact two-key shape it has always had.
    const kind = args.kind ?? task?.teammateMeta?.kind;
    const description = args.description ?? task?.teammateMeta?.description;
    const addressable = args.addressable ?? task?.teammateMeta?.addressable;
    const helperMeta = {
      ...(kind ? { kind } : {}),
      ...(description ? { description } : {}),
      ...(addressable ? { addressable } : {}),
    };
    // A helper has no handle the person chose — its brief is its name.
    const title = kind === "helper" && description ? description : `@${name}`;
    if (!task) {
      state.tasks.set(
        teammateTaskId,
        buildNewTask(teammateTaskId, args.agentSlug ?? "bash", {
          title,
          phase: "idle",
          ...(args.runtime
            ? { runtime: args.runtime as import("@bash/shared").HarnessId }
            : {}),
          teammateMeta: {
            name,
            parentTaskId,
            parentToolUseId: toolUseId,
            agentSlug: args.agentSlug ?? "bash",
            ...(args.agentIcon ? { agentIcon: args.agentIcon } : {}),
            ...(args.agentColor ? { agentColor: args.agentColor } : {}),
            ...helperMeta,
          },
        })
      );
    } else {
      // Upgrade in place — a lane minted from a racing message carries
      // parentToolUseId '' until the real spawn/marker fills it.
      task.teammateMeta = {
        name,
        parentTaskId,
        parentToolUseId: toolUseId || task.teammateMeta?.parentToolUseId || "",
        agentSlug: args.agentSlug ?? task.teammateMeta?.agentSlug ?? "bash",
        ...((args.agentIcon ?? task.teammateMeta?.agentIcon)
          ? { agentIcon: args.agentIcon ?? task.teammateMeta?.agentIcon }
          : {}),
        ...((args.agentColor ?? task.teammateMeta?.agentColor)
          ? { agentColor: args.agentColor ?? task.teammateMeta?.agentColor }
          : {}),
        ...helperMeta,
      };
      // Write only when there is something NEW to say: a helper's brief, once
      // the spawn names it (a lane minted by a racing message is titled
      // '@handle' until then). A teammate's title — server-written, or the one
      // the mint gave it — is never overwritten from here.
      if (kind === "helper" && description) task.title = description;
    }

    const parentTask = state.tasks.get(parentTaskId);
    if (parentTask) {
      (parentTask.teammates ??= {})[teammateTaskId] = {
        name,
        toolUseId:
          toolUseId || parentTask.teammates?.[teammateTaskId]?.toolUseId || "",
      };
    }

    state.teammateParentByTaskId[teammateTaskId] = parentTaskId;
  });
}

/**
 * Ensure the lane AND its thread: registry registration plus a MESSAGE_SYNC
 * fetch when we hold no messages for it (teammate tasks are real tasks — the
 * existing per-task fetch works unchanged). Called from the TeammateCard mount
 * so a lane rendered from a persisted/cached delegated marker (a path no live
 * event or parent sync touches) still hydrates its thread.
 */
export function ensureTeammateLaneWithThread(
  args: EnsureTeammateLaneArgs
): void {
  ensureTeammateLane(args);
  const lane = useTaskStore.getState().tasks.get(args.teammateTaskId);
  if (lane && lane.messages.length > 0) return;
  const socket = getSocket();
  if (socket?.connected) {
    socket.emit(EVENTS.MESSAGE_SYNC_REQUEST, { taskId: args.teammateTaskId });
  }
}

/**
 * Reload durability — rebuild the teammate registry from a parent task's
 * loaded messages (delegated tool_result markers), then fetch each teammate's
 * thread via the EXISTING per-task messages fetch (teammate tasks are real
 * tasks, so MESSAGE_SYNC works unchanged). Fetches only threads we don't
 * already hold, mirroring switchTask's hasMessages guard.
 */
export function rebuildTeammateLanes(
  parentTaskId: string,
  messages: Message[]
): void {
  const spawns = scanDelegatedSpawns(messages);
  if (spawns.length === 0) return;

  for (const spawn of spawns) {
    ensureTeammateLane({
      teammateTaskId: spawn.teammateTaskId,
      name: spawn.name,
      parentTaskId,
      toolUseId: spawn.toolUseId,
      // R76 — no live spawn event arrives for a helper whose turn is already
      // over, so the marker's own marks are what make this a HELPER lane
      // titled by its brief rather than a teammate lane on the handle.
      kind: spawn.kind,
      description: spawn.description,
      addressable: spawn.addressable,
    });
  }

  const socket = getSocket();
  if (!socket?.connected) return;
  const tasks = useTaskStore.getState().tasks;
  for (const spawn of spawns) {
    const lane = tasks.get(spawn.teammateTaskId);
    if (!lane || lane.messages.length === 0) {
      socket.emit(EVENTS.MESSAGE_SYNC_REQUEST, {
        taskId: spawn.teammateTaskId,
      });
    }
  }
}

export function registerTeammateHandlers(socket: Socket): () => void {
  const handleSpawned = (data: SubagentSpawnedPayload) => {
    if (!data?.teammateTaskId || !data.name) return;
    ensureTeammateLane({
      teammateTaskId: data.teammateTaskId,
      name: data.name,
      parentTaskId: data.parentTaskId || data.taskId,
      toolUseId: data.toolUseId,
      agentSlug: data.agentSlug,
      agentIcon: data.agentIcon,
      agentColor: data.agentColor,
      runtime: data.runtime,
      kind: data.kind,
      description: data.description,
      addressable: data.addressable,
    });
  };

  const handleMessage = (data: SubagentMessagePayload) => {
    const {
      taskId,
      direction,
      name,
      peerTaskId,
      text,
      messageId,
      stopReason,
      from,
    } = data ?? {};
    if (!taskId || !messageId || typeof text !== "string") return;

    // A to_teammate message can race the spawn event on reconnect — mint the
    // lane (toolUseId unknown yet; the spawn resync upgrades it) so the row
    // has a thread to land in.
    if (direction === "to_teammate" && peerTaskId) {
      ensureTeammateLane({
        teammateTaskId: taskId,
        name,
        parentTaskId: peerTaskId,
        toolUseId: "",
      });
    }

    // Mirrors the server's persisted row: user-role, peer attribution riding
    // content[0].peer, row id = the client-minted messageId (dedup key).
    const message: Message = {
      id: messageId,
      role: "user",
      content: [
        {
          type: "text",
          text,
          // The same derivation the SERVER's persist makes (spec 2026-09-12), from the
          // same shared function — this row and the one a reload fetches are the same row.
          sender: senderFromPeer(direction, name, from),
          // Phase 2 — a helper's report says WHY it ended. The server stamps
          // the durable row the same way, gated on PRESENCE (an empty reason
          // keeps its key), so the optimistic row and a reload agree.
          peer: {
            direction,
            name,
            peerTaskId,
            ...(stopReason !== undefined ? { stopReason } : {}),
            // Phase 3 — who sent it. Same presence gate: an absent key is the
            // Phase 2 row, which reads as the lead's. The server stamps the
            // durable row identically, so the label cannot flip on reload.
            ...(from !== undefined ? { from } : {}),
          },
        } as unknown as ContentBlock,
      ],
      timestamp: new Date(),
      agentSlug: "user",
      identity: { kind: "persisted", serverId: messageId },
    };

    useTaskStore.getState()._mutateTasks((tasks) => {
      const task = tasks.get(taskId);
      if (!task) return;
      // Optimistic-dedup on messageId — re-emits and MESSAGE_SYNC echoes no-op.
      if (findMessageByAnyId(task.messages, messageId) !== -1) return;
      task.messages.push(message);
      task.lastActivityAt = new Date();
    });
  };

  socket.on(EVENTS.SUBAGENT_SPAWNED, handleSpawned);
  socket.on(EVENTS.SUBAGENT_MESSAGE, handleMessage);

  return () => {
    socket.off(EVENTS.SUBAGENT_SPAWNED, handleSpawned);
    socket.off(EVENTS.SUBAGENT_MESSAGE, handleMessage);
  };
}
